import React from 'react';
import Tooltip from './Tooltip';
import './Toggle.css';

/**
 * Generic single-select toggle.
 * items: [{ value, icon?, label?, tooltip?, ariaLabel?, badge?, disabled? }]
 * Supports icon-only, text-only, both, or empty (no icon/label).
 */
const Toggle = ({
    items = [],
    value,
    onChange,
    ariaLabel,
    className = '',
    variant = 'default',
    disabled = false,
    allowReselect = false,
}) => {
    if (!Array.isArray(items) || items.length === 0) return null;

    const handleClick = (item) => {
        if (disabled || item?.disabled) return;
        if (!allowReselect && item?.value === value) return;
        onChange?.(item?.value);
    };

    const classes = [
        'toggle',
        variant === 'pill' ? 'toggle--pill' : '',
        disabled ? 'toggle--disabled' : '',
        className,
    ].filter(Boolean).join(' ');

    return (
        <div className={classes} role="radiogroup" aria-label={ariaLabel}>
            {items.map((item, idx) => {
                if (!item) return null;
                const key = item.value ?? idx;
                const isActive = item.value === value;
                const hasIcon = Boolean(item.icon);
                const hasLabel = Boolean(item.label);
                const isIconOnly = hasIcon && !hasLabel;
                const hasContent = hasIcon || hasLabel;
                const itemAriaLabel = item.ariaLabel || item.label || item.tooltip || String(item.value ?? idx);

                const button = (
                    <button
                        type="button"
                        className={[
                            'toggle__item',
                            isActive ? 'toggle__item--active' : '',
                            isIconOnly ? 'toggle__item--icon-only' : '',
                            item.disabled ? 'toggle__item--disabled' : '',
                            item.className || '',
                        ].filter(Boolean).join(' ')}
                        onClick={() => handleClick(item)}
                        aria-pressed={isActive}
                        aria-label={itemAriaLabel}
                        disabled={disabled || Boolean(item.disabled)}
                    >
                        {hasIcon ? (
                            <span className="toggle__item-icon">{item.icon}</span>
                        ) : null}
                        {hasLabel ? (
                            <span className="toggle__item-label">{item.label}</span>
                        ) : null}
                        {!hasContent ? (
                            <span className="toggle__item-empty" aria-hidden="true" />
                        ) : null}
                        {Number.isFinite(item.badge) ? (
                            <span className="toggle__item-badge" aria-hidden="true">{item.badge}</span>
                        ) : null}
                    </button>
                );

                return (
                    <React.Fragment key={key}>
                        {item.tooltip ? <Tooltip content={item.tooltip}>{button}</Tooltip> : button}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default Toggle;
